import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Header from "@/components/Header";
import { calcEarnings, nairaFormat, api } from "@/lib/api";
import ImageUploader, { imgUrl } from "@/components/ImageUploader";
import { Plus, Trash2, CheckCircle2, Eye, EyeOff } from "lucide-react";

const PLATFORMS = [
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram" },
  { value: "x", label: "X / Twitter" },
  { value: "youtube", label: "YouTube" },
  { value: "facebook", label: "Facebook" },
];

export default function Register() {
  const { register, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // step 1
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  // step 2
  const [handles, setHandles] = useState([{ platform: "instagram", handle: "", followers: 0, url: "" }]);

  // step 3
  const [bank, setBank] = useState({ account_name: "", account_number: "", bank_name: "" });

  // step 4 (avatar after successful registration)
  const [avatarPath, setAvatarPath] = useState("");
  const [avatarSaving, setAvatarSaving] = useState(false);

  const totalFollowers = handles.reduce((s, h) => s + Number(h.followers || 0), 0);
  const earn = calcEarnings(totalFollowers);

  const addHandle = () => setHandles([...handles, { platform: "tiktok", handle: "", followers: 0, url: "" }]);
  const removeHandle = (i) => setHandles(handles.filter((_, idx) => idx !== i));
  const updateHandle = (i, key, val) => {
    const next = [...handles];
    next[i] = { ...next[i], [key]: val };
    setHandles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register({
      email, password, name, phone,
      social_handles: handles.filter((h) => h.handle).map((h) => ({
        ...h,
        followers: Math.max(0, Math.floor(Number(h.followers) || 0)),
      })),
      bank_details: bank.account_number ? bank : null,
    });
    setLoading(false);
    if (res.ok) setStep(4);  // now authenticated — proceed to photo step
    else setError(res.error);
  };

  const finishWithPhoto = async () => {
    if (!avatarPath) {
      setError("Please upload a profile photo to continue.");
      return;
    }
    setAvatarSaving(true);
    try {
      await api.put("/me/avatar", { path: avatarPath });
      await refresh();
      nav("/dashboard");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save photo. Try again.");
    } finally {
      setAvatarSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030A06]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#00E676]">// Creator application</span>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2 mb-2">Get on the roster</h1>
        <p className="text-[#8A9F90] mb-8">Four quick steps. Nasarawa Expo will verify your handles before activating you.</p>

        {/* progress */}
        <div className="flex gap-2 mb-10">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} data-testid={`register-step-indicator-${s}`} className={`flex-1 h-1 ${step >= s ? "bg-[#00E676]" : "bg-[#122E1C]"}`} />
          ))}
        </div>

        <form onSubmit={submit} className="border border-[#122E1C] bg-[#07140B] p-8 space-y-6">
          {step === 1 && (
            <>
              <h2 className="font-display font-bold text-2xl tracking-tight">1. Tell us who you are</h2>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full name" required value={name} onChange={setName} testid="reg-name" />
                <Field label="Phone (Nigeria)" required value={phone} onChange={setPhone} testid="reg-phone" placeholder="0803…" />
                <Field label="Email" required type="email" value={email} onChange={setEmail} testid="reg-email" />
                <PasswordField label="Password (min 6)" value={password} onChange={setPassword} testid="reg-password" />
              </div>
              <div className="flex justify-end">
                <Button data-testid="reg-step-1-next" type="button" onClick={() => setStep(2)} disabled={!name || !email || !phone || password.length < 6} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
                  Next: social handles →
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display font-bold text-2xl tracking-tight">2. Drop your social handles</h2>
              <p className="text-sm text-[#8A9F90]">Admin will tap each link to verify follower counts.</p>

              <div className="space-y-3">
                {handles.map((h, i) => (
                  <div key={i} data-testid={`reg-handle-row-${i}`} className="grid grid-cols-12 gap-2 items-center border border-[#122E1C] p-3">
                    <select
                      data-testid={`reg-handle-platform-${i}`}
                      value={h.platform}
                      onChange={(e) => updateHandle(i, "platform", e.target.value)}
                      className="col-span-3 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none"
                    >
                      {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <input
                      data-testid={`reg-handle-name-${i}`}
                      placeholder="@handle"
                      value={h.handle}
                      onChange={(e) => updateHandle(i, "handle", e.target.value)}
                      className="col-span-4 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none"
                    />
              <input data-testid={`reg-handle-followers-${i}`} type="number" min="0" step="1" inputMode="numeric" pattern="[0-9]*"
                      placeholder="Followers" value={h.followers} onChange={(e) => updateHandle(i, "followers", e.target.value.replace(/[^0-9]/g, ""))}
                      className="col-span-3 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none font-mono-jb" />
                    <input
                      data-testid={`reg-handle-url-${i}`}
                      placeholder="profile url"
                      value={h.url}
                      onChange={(e) => updateHandle(i, "url", e.target.value)}
                      className="col-span-12 md:col-span-1 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none hidden md:block"
                    />
                    <button type="button" onClick={() => removeHandle(i)} className="col-span-2 md:col-span-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 p-2 flex justify-center" data-testid={`reg-handle-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addHandle} data-testid="reg-handle-add" className="text-[#00E676] text-sm flex items-center gap-1 hover:underline">
                  <Plus className="w-4 h-4" /> Add another handle
                </button>
              </div>

              <div className="border border-[#00E676]/30 bg-[#0D2114] p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Estimated take-home per 10k-follower gig</div>
                <div className="mt-2 grid grid-cols-3 gap-4">
                  <Stat l="Total followers" v={totalFollowers.toLocaleString()} />
                  <Stat l="Estimated gig value" v={nairaFormat(earn.gross)} />
                  <Stat l="You earn (80%)" v={nairaFormat(earn.creative)} accent />
                </div>
              </div>

              <div className="flex justify-between">
                <Button data-testid="reg-step-2-back" type="button" variant="outline" onClick={() => setStep(1)} className="border-[#122E1C] hover:bg-[#0D2114] rounded-none">← Back</Button>
                <Button data-testid="reg-step-2-next" type="button" onClick={() => setStep(3)} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
                  Next: bank details →
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display font-bold text-2xl tracking-tight">3. Where do we send the money?</h2>
              <p className="text-sm text-[#8A9F90]">Bank details are only visible to admin and used for the 80% payout transfer.</p>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Account name" value={bank.account_name} onChange={(v) => setBank({ ...bank, account_name: v })} testid="reg-bank-name" />
                <Field label="Account number" value={bank.account_number} onChange={(v) => setBank({ ...bank, account_number: v })} testid="reg-bank-number" />
                <Field label="Bank" value={bank.bank_name} onChange={(v) => setBank({ ...bank, bank_name: v })} testid="reg-bank-bank" placeholder="e.g. GTBank" />
              </div>

              {error && <div data-testid="register-error" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3">{error}</div>}

              <div className="flex justify-between">
                <Button data-testid="reg-step-3-back" type="button" variant="outline" onClick={() => setStep(2)} className="border-[#122E1C] hover:bg-[#0D2114] rounded-none">← Back</Button>
                <Button data-testid="register-submit-btn" disabled={loading} type="submit" className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
                  {loading ? "Creating account…" : "Continue · upload photo →"}
                </Button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="font-display font-bold text-2xl tracking-tight">4. Add a profile photo</h2>
              <p className="text-sm text-[#8A9F90]">A clear face/brand photo helps brands trust you faster. This is required to finish setup.</p>

              <div className="max-w-xs">
                <ImageUploader
                  testid="reg-avatar"
                  square
                  label="Upload your photo"
                  currentPath={avatarPath}
                  onUploaded={(p) => { setAvatarPath(p); setError(""); }}
                  onClear={() => setAvatarPath("")}
                />
              </div>

              {avatarPath && (
                <div className="flex items-center gap-2 text-[#00E676] text-sm font-mono-jb">
                  <CheckCircle2 className="w-4 h-4" /> Photo uploaded
                </div>
              )}

              {error && <div data-testid="register-error-step4" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3">{error}</div>}

              <div className="flex justify-end">
                <Button
                  data-testid="register-finish-btn"
                  type="button"
                  disabled={avatarSaving || !avatarPath}
                  onClick={finishWithPhoto}
                  className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none disabled:opacity-40"
                >
                  {avatarSaving ? "Finishing…" : "Finish & enter dashboard →"}
                </Button>
              </div>
            </>
          )}
        </form>

        <div className="mt-6 text-sm text-[#8A9F90]">
          Already on the roster? <Link to="/login" className="text-[#00E676] hover:underline" data-testid="register-to-login-link">Sign in →</Link>
        </div>
      </div>
    </div>
  );
}
function PasswordField({ label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{label} *</Label>
      <div className="relative mt-2">
        <Input
          data-testid={testid}
          required
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent border border-[#122E1C] rounded-none focus:border-[#00E676] focus-visible:ring-0 text-white pr-10"
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A9F90] hover:text-[#00E676] p-1"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}



function Field({ label, required, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{label}{required && " *"}</Label>
      <Input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-[#122E1C] rounded-none mt-2 focus:border-[#00E676] focus-visible:ring-0 text-white"
      />
    </div>
  );
}

function Stat({ l, v, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{l}</div>
      <div className={`font-mono-jb text-lg mt-1 ${accent ? "text-[#00E676] font-bold" : "text-white"}`}>{v}</div>
    </div>
  );
}
